import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { SwmmProject } from "../model/inp/types.ts";
import type { SimulationResults } from "../model/results/simulationResults.ts";
import { computeLongitudinalProfile, findProfilePath } from "../engine/longitudinalProfile.ts";
import { unitSystemOf } from "../model/units.ts";
import { useLanguage, useT } from "../i18n/hooks.ts";
import { localeFor } from "../i18n/language.ts";
import { Dialog } from "./Dialog.tsx";
import { DialogActions } from "./DialogActions.tsx";
import { TargetIcon } from "./TargetIcon.tsx";
import {
  CHART_AXIS_LINE,
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_LONGITUDINAL_PROFILE,
  CHART_TOOLTIP_STYLE,
} from "./chartTheme.ts";

/**
 * Node-to-node elevation profile: ground, invert, and (once a simulation
 * has run) the water surface at one report timestamp, plotted against
 * cumulative distance along the shortest BFS-traced path between two
 * nodes — this app's network graph doesn't support picking a specific one
 * of several possible routes.
 *
 * `startNode`/`endNode` are controlled (owned by App.tsx, not local state)
 * so a "pick on the map" round-trip survives this dialog closing:
 * `onPickStart`/`onPickEnd` close it and put App.tsx into a one-click
 * node-picking mode, which reopens this dialog with the picked node
 * already set.
 */
export function LongitudinalProfileDialog({
  project,
  results,
  startNode,
  endNode,
  onStartNodeChange,
  onEndNodeChange,
  onPickStart,
  onPickEnd,
  onClose,
}: {
  project: SwmmProject;
  results: SimulationResults | null;
  startNode: string;
  endNode: string;
  onStartNodeChange: (name: string) => void;
  onEndNodeChange: (name: string) => void;
  onPickStart: () => void;
  onPickEnd: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const language = useLanguage();
  const nodeNames = useMemo(() => project.nodes.map((n) => n.name), [project]);
  const [timeIndex, setTimeIndex] = useState(0);

  const sys = unitSystemOf(project.flowUnits);
  const lengthUnit = sys === "US" ? "ft" : "m";

  const path = useMemo(
    () => findProfilePath(project, startNode, endNode),
    [project, startNode, endNode]
  );
  const profile = useMemo(
    () =>
      path
        ? computeLongitudinalProfile(project, path, results?.nodeHead ?? new Map(), timeIndex)
        : [],
    [project, path, results, timeIndex]
  );

  const data = profile.map((p) => ({
    node: p.node,
    distance: Number(p.distance.toFixed(2)),
    [t("longitudinalProfile.ground")]: p.ground,
    [t("longitudinalProfile.invert")]: p.invert,
    ...(p.head !== null ? { [t("longitudinalProfile.waterSurface")]: p.head } : {}),
  }));

  const currentTime = results?.times[timeIndex];
  const timeLabel = currentTime
    ? currentTime.toLocaleString(localeFor(language), {
        month: "short", // Not "numeric" — "1/6" reads as ambiguous.
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "UTC", // Simulation timestamps are UTC-anchored; display must match.
      })
    : "";

  // Audit finding: the fixed "Perfil longitudinal" title didn't say WHICH
  // profile was on screen. Show the start→end node pair the chart is
  // actually drawn from so the reader can tell one profile from another
  // without hunting through the two dropdowns below — falls back to the
  // generic label only when there's no real endpoint pair to name yet.
  const dialogTitle =
    startNode && endNode
      ? t("longitudinalProfile.titleWithNodes", { start: startNode, end: endNode })
      : t("longitudinalProfile.title");

  return (
    <Dialog title={dialogTitle} onClose={onClose} wide fitContent>
      <div className="options-section">
        <label className="options-field">
          {t("longitudinalProfile.startNode")}
          <div className="options-field-row">
            <select value={startNode} onChange={(e) => onStartNodeChange(e.target.value)}>
              {nodeNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="button-icon"
              onClick={onPickStart}
              aria-label={t("longitudinalProfile.pickOnMap")}
              title={t("longitudinalProfile.pickOnMap")}
            >
              <TargetIcon />
            </button>
          </div>
        </label>
        <label className="options-field">
          {t("longitudinalProfile.endNode")}
          <div className="options-field-row">
            <select value={endNode} onChange={(e) => onEndNodeChange(e.target.value)}>
              {nodeNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="button-icon"
              onClick={onPickEnd}
              aria-label={t("longitudinalProfile.pickOnMap")}
              title={t("longitudinalProfile.pickOnMap")}
            >
              <TargetIcon />
            </button>
          </div>
        </label>
      </div>

      {!path ? (
        <p className="property-panel-hint">{t("longitudinalProfile.noPath")}</p>
      ) : (
        <>
          {results && results.times.length > 0 && (
            <div className="longitudinal-profile-time">
              <span className="longitudinal-profile-time-label">{timeLabel}</span>
              <input
                type="range"
                min={0}
                max={results.times.length - 1}
                value={timeIndex}
                onChange={(e) => setTimeIndex(Number(e.target.value))}
                className="longitudinal-profile-time-slider"
              />
            </div>
          )}
          <div className="longitudinal-profile-chart">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data} margin={{ top: 4, right: 16, bottom: 20, left: 0 }}>
                <CartesianGrid {...CHART_GRID_PROPS} />
                {/* `type="number"` promotes the X axis from Recharts' default
                  categorical scale (one tick per real data point — so a
                  400m profile listed every manhole distance as a tick:
                  0 / 34.95 / 70.55 / 84.14 / 170.06 / … per audit finding)
                  to a continuous linear scale. Upper bound is a function
                  (not `"auto"`, live feedback): Recharts' own tick-niceing
                  for a `domain={["auto","auto"]}` + `tickCount={6}` pair
                  overshot a real 556.73m profile all the way to a 750m
                  axis max — nearly 200m of axis with no line anywhere near
                  it, confirmed empirically (Victoria's own SAN_SMH000365 →
                  SAN_SMH002873 profile) rather than assumed. Rounding the
                  real `dataMax` up to the nearest 50 keeps a round number
                  without Recharts' own algorithm free to wander that far
                  past the actual data; the Y axis below doesn't have this
                  problem (nowhere near as wide a magnitude range) so it
                  keeps plain `"auto"`. Exact per-manhole distance stays
                  available in the tooltip either way. */}
                <XAxis
                  type="number"
                  dataKey="distance"
                  domain={[0, (dataMax: number) => Math.ceil(dataMax / 50) * 50]}
                  tickCount={6}
                  tick={CHART_AXIS_TICK}
                  axisLine={CHART_AXIS_LINE}
                  tickLine={CHART_AXIS_LINE}
                  label={{
                    value: `${t("longitudinalProfile.distance")} (${lengthUnit})`,
                    position: "insideBottom",
                    offset: -2,
                    fontSize: 11,
                  }}
                />
                <YAxis
                  tick={CHART_AXIS_TICK}
                  axisLine={CHART_AXIS_LINE}
                  tickLine={CHART_AXIS_LINE}
                  domain={["auto", "auto"]}
                  tickCount={6}
                  tickFormatter={(value: number) =>
                    // `maximumFractionDigits` alone (no `minimumFractionDigits`)
                    // trims trailing zeros the same way the old `Number(...
                    // toFixed(2))` round-trip did — `formatNumber`'s own fixed
                    // digit count doesn't fit an axis tick.
                    new Intl.NumberFormat(localeFor(language), { maximumFractionDigits: 2 }).format(
                      value
                    )
                  }
                  label={{
                    value: lengthUnit,
                    angle: -90,
                    position: "insideLeft",
                    fontSize: 11,
                  }}
                />
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.node ?? ""}
                />
                {/* `verticalAlign="top"` — live feedback: Recharts' default
                    bottom-aligned legend renders `position: absolute;
                    bottom: <margin.bottom>px`, i.e. INSIDE that same
                    bottom-margin band the X axis's own "insideBottom"
                    title also lives in, by construction — no margin.bottom
                    value fixes the overlap between them since both scale
                    together (confirmed empirically: the gap stayed
                    constant across several margin values). Top avoids the
                    conflict entirely instead of fighting for shared
                    real estate. */}
                <Legend verticalAlign="top" wrapperStyle={{ fontSize: "0.75rem" }} />
                {/* Semantic domain colors (brown = soil, dark = pipe
                  infrastructure, blue = water), NOT positions in the
                  categorical `CHART_PALETTE` — a reader has to identify
                  the ground line as ground and the water line as water
                  without a legend. `.water` reuses `CHART_SERIES_PRIMARY`
                  (Okabe-Ito blue) so this chart aligns with every other
                  chart's own "data blue" instead of the ad-hoc
                  Tailwind-ish tone the pre-audit version used. */}
                <Line
                  type="linear"
                  dataKey={t("longitudinalProfile.ground")}
                  stroke={CHART_LONGITUDINAL_PROFILE.ground}
                  dot={{ r: 2 }}
                  strokeWidth={1.5}
                />
                <Line
                  type="linear"
                  dataKey={t("longitudinalProfile.invert")}
                  stroke={CHART_LONGITUDINAL_PROFILE.invert}
                  dot={{ r: 2 }}
                  strokeWidth={1.5}
                />
                {results && (
                  <Line
                    type="linear"
                    dataKey={t("longitudinalProfile.waterSurface")}
                    stroke={CHART_LONGITUDINAL_PROFILE.water}
                    dot={{ r: 2 }}
                    strokeWidth={2}
                    connectNulls
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* Audit finding: with only "Aceptar" wired, the only other way out
          was a backdrop click — which isn't a discoverable close on a
          modal that also has real interactive dropdowns/slider inside
          (a click there could easily land on the backdrop by accident,
          or the user just doesn't think of clicking outside). Adds an
          explicit "Cerrar" alongside the primary, using DialogActions'
          own `onClose` slot so it inherits the same right-align +
          cancel-first layout every other dialog uses. */}
      <DialogActions
        primary={{ label: t("common.ok"), onClick: onClose }}
        onClose={onClose}
        closeLabel={t("common.close")}
      />
    </Dialog>
  );
}
